import { useMemo } from "react";
import { DateTime } from "luxon";
import { useDroppable } from "@dnd-kit/core";
import { CalendarCog, CalendarDays, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { isCalendarColumnId, parseCalendarColumnId } from "@/lib/calendars";
import CalendarEventItem from "../calendar-event-item";
import { computeEventColumnLayout, isCancelledCalendarEvent } from "../calendar-utils";
import { eventGridPositionForAxis } from "../calendar-time-axis";
import { fitSpanMinutes } from "../calendar-fit-zoom";
import { measureColumnGeometry } from "../calendar-drag-geometry";
import { BlockOverlay } from "../calendar-blocks";
import { SessionRail } from "./session-rail";

// Default start for the title-row "+" on a standalone-calendar column: the next
// quarter hour when the pane shows today, otherwise 09:00. The entry sheet snaps
// and clamps the minute into the visible window itself.
const DEFAULT_ENTRY_MINUTE = 9 * 60;
function defaultEntryMinuteForDay(dayKey) {
  const now = DateTime.now();
  if (dayKey !== now.toISODate()) return DEFAULT_ENTRY_MINUTE;
  return Math.ceil((now.hour * 60 + now.minute) / 15) * 15;
}

export function DayTenantColumn({
  events,
  tenantTitle,
  use12HourFormat,
  timeAxis,
  selectedEventId,
  setSelectedEventId,
  /** Deep-linked appointment ("show this on the calendar") — ringed and raised above its neighbours. */
  highlightedEventId = null,
  currentTimeRowStart,
  currentTimeRowOffsetPercent,
  nowAnchorRef,
  attachNowScrollRef,
  overlayBlocks,
  dayKey,
  columnId,
  groupBy,
  dragPlaceholder = null,
  /** Which day pane this column belongs to (two-day split); keeps droppable ids unique. */
  paneIndex = 0,
  /** Sticky offset for the tenant-title row — pushed down when the day-pane headers row is present. */
  titleStickyTopClass = "top-8",
  /** Session rail (background who-is-where indicator) view preferences. */
  showSessionRail = false,
  railWidthPx = 26,
  railColorMode = "accent",
  railMarginPx = 0,
  /**
   * Opens the per-day session override dialog for this column's doctor
   * (doctor-grouped view only). Wired only when the caller may manage
   * scheduling; renders a hover-revealed icon button in the title row.
   */
  onSessionAction,
  /** Accent of a standalone-calendar column (its calendar colour) — tints the title icon. */
  columnColorHex = "",
  /**
   * (minute) → open the "New entry" sheet for this standalone-calendar column at
   * that time-of-day. Wired only for calendar columns when the caller may manage
   * the calendar: enables the title-row "+" and double-click-on-empty-time.
   */
  onCreateEntry,
}) {
  // A standalone admin calendar rendered as its own column ("cal:<id>"): no
  // doctor/clinic behind it, so no session rail or session overrides, and its
  // events are direct entries created straight into the calendar.
  const isCalendarColumn = isCalendarColumnId(columnId);
  const calendarId = parseCalendarColumnId(columnId);
  const canCreateEntry = isCalendarColumn && typeof onCreateEntry === "function";
  // The rail is a per-entity background reference, so it only makes sense on a
  // titled clinic/doctor column — never the merged "_all" column (which holds
  // everyone) nor a standalone-calendar column. Off entirely when disabled or
  // collapsed to zero width.
  const railEnabled = Boolean(
    showSessionRail && railWidthPx > 0 && columnId && columnId !== "_all" && !isCalendarColumn,
  );
  // Memoized on the (identity-stable) per-column events array — without this
  // the fresh sorted copy defeats the layout memo below and every calendar
  // re-render (each 5-min drag-preview step!) re-runs the overlap layout for
  // every column.
  const sortedEvents = useMemo(
    () => [...events].sort((left, right) => left.startAt.toMillis() - right.startAt.toMillis()),
    [events],
  );
  const eventLayout = useMemo(() => computeEventColumnLayout(sortedEvents), [sortedEvents]);

  // Whole-day booked total for this column's clinic/doctor — the header counterpart
  // to the per-session counts on the rail. Cancelled bookings never count, even
  // when the "Show cancelled" toggle puts them back in `events`.
  const bookedCount = useMemo(
    () =>
      events.reduce(
        (total, event) =>
          event?.kind === "appointment" && !isCancelledCalendarEvent(event) ? total + 1 : total,
        0,
      ),
    [events],
  );

  // Snapped landing preview for an in-flight appointment drag targeting THIS column.
  const activePlaceholder =
    dragPlaceholder &&
    dragPlaceholder.dayKey === dayKey &&
    (dragPlaceholder.columnId ?? null) === (columnId && columnId !== "_all" ? columnId : null)
      ? dragPlaceholder
      : null;

  // Whole-column drop surface for appointment drag-move. The id doubles as the
  // `data-time-col` marker the drag geometry measures (rect spans the h-7
  // header + the fine grid; see measureColumnGeometry). Split columns carry
  // their tenant/doctor id so a cross-column drop reassigns ownership. The pane
  // index keeps ids unique when both day panes show the same date.
  const droppableId = `col:${dayKey}:${columnId || "_all"}:${paneIndex}`;
  const { isOver, setNodeRef } = useDroppable({
    id: droppableId,
    data: {
      type: "column",
      dayKey,
      columnId: columnId && columnId !== "_all" ? columnId : null,
      groupBy,
      // Column identity for the DnD gates (canDropOnColumn): standalone entries only
      // land on their own calendar column; doctor/clinic appointments never land on one.
      kind: isCalendarColumn ? "calendar" : columnId && columnId !== "_all" ? "entity" : "all",
      calendarId: calendarId || null,
    },
  });

  // Double-click on empty time in a standalone-calendar column starts a new entry
  // there. Cards, the title row and the all-day strip keep their own double-click
  // (the editor / nothing); the pointer's Y maps to a minute through the same
  // measured geometry the drag path uses, so zoom and row stretching are absorbed.
  const handleColumnDoubleClick = (event) => {
    if (!canCreateEntry) return;
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest("[data-calendar-event-card], [data-column-title], button, a")) return;
    const geometry = measureColumnGeometry(event.currentTarget, timeAxis);
    if (!geometry || !Number.isFinite(event.clientY)) return;
    const minute = geometry.minuteAtClientY(event.clientY);
    if (minute < timeAxis.startMinutes || minute >= timeAxis.endMinutes) return;
    // A double-click selects text under the pointer; drop it so the sheet doesn't
    // open over a stray blue smear.
    window.getSelection?.()?.removeAllRanges();
    onCreateEntry(minute);
  };

  return (
    <div
      ref={setNodeRef}
      data-time-col={droppableId}
      onDoubleClick={canCreateEntry ? handleColumnDoubleClick : undefined}
      title={canCreateEntry ? "Double-click an empty time to add an entry" : undefined}
      className={cn(
        "grid min-h-0 min-w-0 flex-1 grid-cols-1 grid-rows-1",
        isOver && "bg-primary/5",
        isCalendarColumn && "select-none",
      )}
    >
      {/* Background grid: one row per tick interval with horizontal dividers.
          Hour labels live in the outer HourLabelsGutter so they stay sticky-left
          during horizontal scroll. */}
      <div
        style={{ gridTemplateRows: timeAxis.tickRowTemplate }}
        className="col-start-1 col-end-2 row-start-1 grid divide-y divide-border/60"
      >
        <div className="row-end-1 h-7" />
        {timeAxis.ticks.map((tick) => (
          <div key={tick.key} />
        ))}
      </div>

      {/* Background session rail: which doctor/clinic is running a session in this
          column, at a glance. Sits behind the events (below, since the events list
          is inset by the rail width) and never intercepts pointer events. */}
      {railEnabled ? (
        <SessionRail
          events={events}
          groupBy={groupBy}
          timeAxis={timeAxis}
          railWidthPx={railWidthPx}
          railMarginPx={railMarginPx}
          colorMode={railColorMode}
        />
      ) : null}

      <ol
        style={{
          gridTemplateRows: timeAxis.fineRowTemplate,
          // Inset the events by the rail's full footprint (left margin + width) so
          // cards start after the rail and never sit on top of it. Padding on the
          // grid container shrinks the single column track, so every %-positioned
          // card/now-line follows.
          ...(railEnabled ? { paddingLeft: `${railMarginPx + railWidthPx}px` } : {}),
        }}
        className="col-start-1 col-end-2 row-start-1 grid grid-cols-1"
      >
        <li
          data-column-title=""
          // An own (possibly empty) title stops the column's double-click hint from
          // showing over the name; the buttons inside carry their own titles.
          title={canCreateEntry ? tenantTitle || "" : undefined}
          className={`group/coltitle col-start-1 col-end-2 flex h-7 shrink-0 items-center justify-center border-b border-border/50 px-1 text-center text-xs font-semibold text-foreground ${tenantTitle
            ? `sticky ${titleStickyTopClass} z-30 bg-background shadow-[0_1px_0_0_hsl(var(--border))]`
            : "bg-muted/15"
            }`}
        >
          {tenantTitle && isCalendarColumn ? (
            <CalendarDays
              className="mr-1 size-3.5 shrink-0 text-muted-foreground"
              style={columnColorHex ? { color: columnColorHex } : undefined}
              aria-hidden
            />
          ) : null}
          {tenantTitle ? <span className="truncate">{tenantTitle}</span> : null}
          {tenantTitle && bookedCount > 0 ? (
            <span
              title={`${bookedCount} patient${bookedCount === 1 ? "" : "s"} booked today`}
              className="ml-1 shrink-0 rounded-sm bg-foreground/10 px-1 font-mono text-[10px] font-medium tabular-nums text-muted-foreground"
            >
              {bookedCount}
            </span>
          ) : null}
          {tenantTitle && onSessionAction && columnId && columnId !== "_all" && !isCalendarColumn ? (
            <button
              type="button"
              onClick={() => onSessionAction(columnId)}
              title="Session overrides for this day…"
              aria-label="Session overrides for this day"
              className="ml-1 inline-flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover/coltitle:opacity-100"
            >
              <CalendarCog className="size-3.5" />
            </button>
          ) : null}
          {tenantTitle && canCreateEntry ? (
            <button
              type="button"
              onClick={() => onCreateEntry(defaultEntryMinuteForDay(dayKey))}
              title="Add an entry to this calendar…"
              aria-label={`Add an entry to ${tenantTitle}`}
              className="ml-1 inline-flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover/coltitle:opacity-100"
            >
              <Plus className="size-3.5" />
            </button>
          ) : null}
        </li>

        {Array.isArray(overlayBlocks) && overlayBlocks.length > 0 ? (
          <li
            style={{ gridRow: `2 / ${2 + timeAxis.fineCount}` }}
            className="pointer-events-none relative z-0 col-start-1 col-end-2"
          >
            <BlockOverlay blocks={overlayBlocks} />
          </li>
        ) : null}

        {currentTimeRowStart != null ? (
          <li
            style={{ gridRow: `${currentTimeRowStart} / span 1` }}
            className="pointer-events-none relative z-30 col-start-1 col-end-2"
          >
            <div
              ref={attachNowScrollRef ? nowAnchorRef : undefined}
              style={{ top: `${currentTimeRowOffsetPercent}%` }}
              className="absolute left-0 right-0 border-t-2 border-primary/90"
            />
            <span
              style={{ top: `${currentTimeRowOffsetPercent}%` }}
              className="absolute -left-1 size-3 -translate-y-1/2 rounded-full border border-background bg-primary shadow-sm"
            />
          </li>
        ) : null}

        {activePlaceholder ? (
          <li
            style={{ gridRow: `${activePlaceholder.startRow} / span ${activePlaceholder.span}` }}
            className="pointer-events-none relative z-40 col-start-1 col-end-2 mt-px flex"
          >
            {/* Tinted to the dragged card's palette so the landing spot reads as
                "this appointment goes here" rather than a generic highlight. The
                border tint must go through the local `--border` var: the unlayered
                `* { @apply border-border }` in app.css outranks border-color
                utilities but reads from that var. */}
            <div
              style={activePlaceholder.theme || undefined}
              className={cn(
                "absolute inset-x-[3px] top-0.5 bottom-1 rounded-xl border-2 border-dashed",
                activePlaceholder.theme
                  ? "[--border:color-mix(in_oklab,var(--event-text-light)_60%,transparent)] dark:[--border:color-mix(in_oklab,var(--event-text-dark)_60%,transparent)] bg-(--event-bg-light)/80 dark:bg-(--event-bg-dark)/80"
                  : activePlaceholder.newPatient
                    ? "[--border:color-mix(in_oklab,var(--color-violet-500)_70%,transparent)] bg-violet-500/10"
                    : "[--border:color-mix(in_oklab,var(--color-emerald-500)_70%,transparent)] bg-emerald-500/10",
              )}
            />
          </li>
        ) : null}

        {sortedEvents.map((event) => {
          const position = eventGridPositionForAxis(event, timeAxis);
          const layout = eventLayout[event.id] || { columnIndex: 0, totalColumns: 1 };
          const leftPercent = (layout.columnIndex / layout.totalColumns) * 100;
          const widthPercent = 100 / layout.totalColumns;
          const isHighlighted = Boolean(highlightedEventId) && highlightedEventId === event.id;
          return (
            <li
              key={event.id}
              data-event-id={event.id}
              data-span-minutes={fitSpanMinutes(event, position) ?? undefined}
              style={{ gridRow: `${position.startRow} / span ${position.span}` }}
              // Empty title: the column's "double-click an empty time" hint must not
              // pop up while hovering a card (the card is not empty time).
              title={canCreateEntry ? "" : undefined}
              className="relative col-start-1 col-end-2 mt-px flex"
            >
              <CalendarEventItem
                event={event}
                use12HourFormat={use12HourFormat}
                variant="full"
                as="button"
                isSelected={selectedEventId === event.id}
                isHighlighted={isHighlighted}
                onClick={setSelectedEventId}
                timeAxis={timeAxis}
                style={{
                  left: `calc(${leftPercent}% + 3px)`,
                  width: `calc(${widthPercent}% - 6px)`,
                  // The deep-linked card climbs above overlapping lanes so nothing clips
                  // its ring (inline z-index beats any class the item sets).
                  zIndex: isHighlighted ? 40 : 20 + layout.columnIndex,
                }}
              />
            </li>
          );
        })}
      </ol>
    </div>
  );
}
